import type { RequestInfo } from '@warp-drive/core/types/request';
import { buildBaseURL, type QueryUrlOptions } from '@warp-drive/utilities';
import { SkipCache } from '@warp-drive/core-types/request';
import { SkipErrorToast } from '../request-options.ts';

const resolutionsUrlOptions: QueryUrlOptions = {
  identifier: { type: 'resolutions' },
  op: 'query',
  resourcePath: 'api/v2/resolutions',
};

/** The balance an existing loan would carry if one held loan replaced it. */
export interface LoanReplacementPreview {
  existingLoanId: string;
  /** Cents, computed by the same rule the answer applies; null when the server has no balance. */
  becomesBalance: number | null;
}

/** A loan the upload found but could not place, parked until the member answers. */
export interface HeldLoan {
  holdId: string;
  /** The held loan's own question; the page is opened from one of them. */
  resolutionId: string;
  /** `held` until answered; `answered` once recorded, until reflection applies it; then `applied`. */
  state: string;
  /** Set once answered: `same` replaces an existing loan, `different` adds it as a new loan. */
  verdict: 'same' | 'different' | null;
  /** The existing loan a recorded answer replaces; null for a new loan or before an answer. */
  answeredLoanId: string | null;
  /** ISO 8601 time the answer was recorded, not applied; null before an answer and after a re-ask. */
  answeredAt: string | null;
  /** True when Tuition.io recorded the answer for the member; null whenever `answeredAt` is null. */
  answeredOnBehalf: boolean | null;
  appliedLoanId: string | null;
  /** The name the loan will carry, as the document spelled it. */
  name: string;
  /** Verbatim loan type text off the document. */
  rawType: string | null;
  typeLabel: string | null;
  /** `YYYY-MM-DD`. */
  disbursedOn: string | null;
  /** Cents. */
  currentBalance: number | null;
  servicerName: string | null;
  /** One per existing loan on the account, in `existingLoans` order. */
  previews: LoanReplacementPreview[];
}

/** A kept loan on the held loans' account, which a held loan may replace. */
export interface ExistingLoan {
  loanId: string;
  name: string;
  typeLabel: string | null;
  /** `YYYY-MM-DD`. */
  disbursedOn: string | null;
  /** Cents. */
  currentBalance: number | null;
  /** True once another extraction of the same document landed on it; strict 1:1 refuses it. */
  inUse: boolean;
}

/** The member's "Verify Your Loans" page for one document and one account. */
export interface LoanVerification {
  /** The question the page was opened from; answers post to it. */
  resolutionId: string;
  documentId: string;
  accountId: string;
  servicerName: string | null;
  heldLoans: HeldLoan[];
  existingLoans: ExistingLoan[];
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function asList(value: unknown) {
  return Array.isArray(value) ? value.map(asRecord) : [];
}

function asId(value: unknown) {
  if (typeof value === 'string' && value) return value;
  if (typeof value === 'number') return String(value);
  return null;
}

function asText(value: unknown) {
  return typeof value === 'string' && value ? value : null;
}

function asCents(value: unknown) {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function asFlag(value: unknown) {
  return typeof value === 'boolean' ? value : null;
}

function asVerdict(value: unknown): HeldLoan['verdict'] {
  return value === 'same' || value === 'different' ? value : null;
}

function parseHeldLoan(raw: Record<string, unknown>): HeldLoan | null {
  const holdId = asId(raw['hold-id']);
  const resolutionId = asId(raw['resolution-id']);
  if (!holdId || !resolutionId) return null;

  return {
    holdId,
    resolutionId,
    state: asText(raw['state']) ?? 'held',
    verdict: asVerdict(raw['verdict']),
    answeredLoanId: asId(raw['answered-loan-id']),
    answeredAt: asText(raw['answered-at']),
    answeredOnBehalf: asFlag(raw['answered-on-behalf']),
    appliedLoanId: asId(raw['applied-loan-id']),
    name: asText(raw['name']) ?? '',
    rawType: asText(raw['raw-type']),
    typeLabel: asText(raw['type-label']),
    disbursedOn: asText(raw['disbursed-on']),
    currentBalance: asCents(raw['current-balance']),
    servicerName: asText(raw['servicer-name']),
    previews: asList(raw['previews']).flatMap((preview) => {
      const existingLoanId = asId(preview['existing-loan-id']);
      return existingLoanId
        ? [{ existingLoanId, becomesBalance: asCents(preview['becomes-balance']) }]
        : [];
    }),
  };
}

function parseExistingLoan(raw: Record<string, unknown>): ExistingLoan | null {
  const loanId = asId(raw['loan-id']);
  if (!loanId) return null;

  return {
    loanId,
    name: asText(raw['name']) ?? '',
    typeLabel: asText(raw['type-label']),
    disbursedOn: asText(raw['disbursed-on']),
    currentBalance: asCents(raw['current-balance']),
    inUse: raw['in-use'] === true,
  };
}

/**
 * Maps the `loan-verifications` JSON:API document onto {@link LoanVerification}.
 *
 * The v2 kit always emits dasherized keys, top level and nested alike, and the member app's
 * key-format negotiation skips the pipeline's camelization, so this reads dasherized names the
 * way `parseSlrAlerts` does. Ids come back as strings and amounts stay in cents.
 *
 * @throws when the document has no resource id or no document id — nothing on the page could be
 *   answered without them
 */
export function parseLoanVerification(content: unknown): LoanVerification {
  const data = asRecord(asRecord(content)['data']);
  const attributes = asRecord(data['attributes']);
  const resolutionId = asId(data['id']);
  const documentId = asId(attributes['document-id']);
  if (!resolutionId || !documentId) {
    throw new Error('Loan verification response is missing its question or document id');
  }

  return {
    resolutionId,
    documentId,
    accountId: asId(attributes['account-id']) ?? '',
    servicerName: asText(attributes['servicer-name']),
    heldLoans: asList(attributes['held-loans']).flatMap((raw) => parseHeldLoan(raw) ?? []),
    existingLoans: asList(attributes['existing-loans']).flatMap(
      (raw) => parseExistingLoan(raw) ?? []
    ),
  };
}

/**
 * Throws unless `resolutionId` is a resolution's numeric id. The id becomes a path segment, and a
 * dot segment such as `..` passes through `encodeURIComponent`, so once the URL is resolved the
 * request would reach a different endpoint.
 *
 * @param context the calling builder's name, for the error
 * @throws Error when the id is not all digits
 */
export function assertResolutionId(resolutionId: string, context: string): void {
  if (!/^\d+$/.test(resolutionId)) {
    throw new Error(
      `${context}: a numeric resolution id is required, received ${JSON.stringify(resolutionId)}`
    );
  }
}

/**
 * GET /api/v2/resolutions/:id/loan-verification — the signed-in member's own held loans on one
 * uploaded document and account, the account's loans each may replace (with an in-use flag), and
 * the balance every pairing would leave. Read it with {@link parseLoanVerification}.
 *
 * Never cached: the page re-reads it after a conflicting answer. The failed-request toast is
 * skipped because every caller handles the failure in place; an expired session still logs out.
 *
 * @throws Error before any request when the id is not numeric, see {@link assertResolutionId}
 * @throws 404 when the id is not an open loan identity question on the caller's own document —
 *   another member's question reads exactly like a missing one
 */
export function getLoanVerificationRequest(resolutionId: string): RequestInfo {
  assertResolutionId(resolutionId, 'getLoanVerificationRequest');
  return {
    url: `${buildBaseURL(resolutionsUrlOptions)}/${encodeURIComponent(resolutionId)}/loan-verification`,
    method: 'GET',
    cacheOptions: { [SkipCache]: true },
    options: { [SkipErrorToast]: true },
  };
}
